import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CommonDataTable from "../../components/CommonDataTable";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import { toast } from "../../components/AppToast";
import { fetchGatePassList } from "../../services/gatePass/gatePass";

function isGatePassRowApproved(row) {
  return String(row?.status ?? "").trim().toLowerCase() === "approved";
}

const baseColumns = [
  { field: "gate_pass_no", headerName: "Gate Pass No", width: 140 },
  { field: "gate_pass_date", headerName: "Gate Pass Date", width: 130 },
  { field: "warehouse", headerName: "Warehouse ID", width: 160 },
  { field: "vendor_name", headerName: "Vendor Name", width: 140 },
  { field: "vehicle_no", headerName: "Vehicle No", width: 120 },
  { field: "status", headerName: "Status", width: 110 },
  { field: "created_at", headerName: "Created At", width: 180 },
];

export default function GatePassList({ onCreateClick, onView, onEdit, refreshKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [listLoading, setListLoading] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const menuRowRef = useRef(null);

  const loadData = useCallback(async () => {
    setListLoading(true);
    try {
      const { rows: apiRows, total: apiTotal } = await fetchGatePassList({
        page: page + 1,
        limit: pageSize,
      });
      setRows(apiRows);
      setTotal(typeof apiTotal === "number" ? apiTotal : apiRows.length);
    } catch (err) {
      toast.error(err?.message || "Failed to load gate passes");
      setRows([]);
      setTotal(0);
    } finally {
      setListLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  const handleDataRefresh = async () => {
    await loadData();
  };

  const actionColumn = useMemo(
    () => ({
      field: "__actions",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            menuRowRef.current = params.row;
            setMenuRow(params.row);
            setMenuAnchorEl(e.currentTarget);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    }),
    []
  );

  return (
    <>
      {listLoading && (
        <p className="text-muted small mb-2 mb-md-0">Loading gate passes…</p>
      )}
      <CommonDataTable
        id="gate-pass-list"
        allPossibleColumns={[actionColumn, ...baseColumns]}
        initialVisible={["__actions", ...baseColumns.map((c) => c.field)]}
        initialRows={rows}
        rowCount={total}
        page={page}
        pageSize={pageSize}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setPage(0);
        }}
        height={500}
        showCreateButton
        onCreateClick={onCreateClick}
        checkboxSelection
        enableImportButton={false}
        enableExportButton={false}
        forInventory
        onDataRefresh={handleDataRefresh}
        readOnly
      />

      <Menu
        open={Boolean(menuAnchorEl)}
        anchorEl={menuAnchorEl}
        onClose={() => {
          setMenuAnchorEl(null);
          menuRowRef.current = null;
        }}
      >
        <MenuItem
          onClick={() => {
            const row = menuRowRef.current ?? menuRow;
            setMenuAnchorEl(null);
            menuRowRef.current = null;
            if (row) onView?.(row);
          }}
        >
          <i className="bi bi-eye me-2" />
          View
        </MenuItem>
        <MenuItem
          disabled={isGatePassRowApproved(menuRowRef.current ?? menuRow)}
          onClick={() => {
            const row = menuRowRef.current ?? menuRow;
            setMenuAnchorEl(null);
            menuRowRef.current = null;
            if (row && !isGatePassRowApproved(row)) onEdit?.(row);
          }}
        >
          <EditIcon fontSize="small" style={{ marginRight: 8 }} />
          Edit
        </MenuItem>
      </Menu>
    </>
  );
}
